import type { EntityStateName } from '../config/tuning';

/**
 * Jump (camera-and-controls.md amendment, 2026-10-16) — the client's copy of
 * `backend/src/game/dungeon-prototype/sim/jump.ts`. No shared package, so the
 * numbers are mirrored by hand, same as `config/tuning.ts`.
 *
 * The jump is cosmetic in the simulation: no i-frames, no effect on collision,
 * speed or noise. The server prices it (stamina, cooldown) and stamps
 * `PlayerState.jumpStartedAt`; every client draws the same arc from that one
 * number. The local player draws from its own predicted takeoff instead, so
 * Space answers on the frame it is pressed (`LocalJump`).
 */

export const JUMP_DURATION_MS = 550;
export const JUMP_HEIGHT_M = 0.45;
export const JUMP_COOLDOWN_MS = JUMP_DURATION_MS + 100;
/** Mirrors the server's `NET.JUMP_COST`. Checked client-side only to avoid predicting a jump the server will refuse. */
export const JUMP_COST = 5;

const CAN_JUMP_FROM: ReadonlySet<string> = new Set<EntityStateName>(['IDLE', 'MOVING', 'SPRINTING']);

/** Height above the floor `elapsedMs` after takeoff: 4h·t(1−t). Zero outside the airtime. */
export function jumpOffsetY(elapsedMs: number, durationMs: number = JUMP_DURATION_MS, heightM: number = JUMP_HEIGHT_M): number {
  if (!(elapsedMs > 0) || elapsedMs >= durationMs) return 0;
  const t = elapsedMs / durationMs;
  return 4 * heightM * t * (1 - t);
}

export function isAirborne(elapsedMs: number, durationMs: number = JUMP_DURATION_MS): boolean {
  return elapsedMs > 0 && elapsedMs < durationMs;
}

/** A synced `jumpStartedAt` of 0 means "never jumped". */
export function remoteJumpOffset(nowServerMs: number, jumpStartedAt: number): number {
  return jumpStartedAt > 0 ? jumpOffsetY(nowServerMs - jumpStartedAt) : 0;
}

/** Whether a synced `jumpStartedAt` change is fresh enough to be worth animating — not a stale value on first sight. */
export function isFreshJump(nowServerMs: number, jumpStartedAt: number): boolean {
  return jumpStartedAt > 0 && nowServerMs - jumpStartedAt < JUMP_DURATION_MS;
}

/**
 * The local player's predicted jump. One per run; times are the estimated
 * server clock, so the same `now` drives the arc and the cooldown.
 */
export class LocalJump {
  private startedAt = 0;

  /** Starts a jump if the server would accept one; returns whether it did. */
  tryStart(now: number, state: string, stamina: number): boolean {
    if (!CAN_JUMP_FROM.has(state)) return false;
    if (this.startedAt > 0 && now - this.startedAt < JUMP_COOLDOWN_MS) return false;
    if (stamina < JUMP_COST) return false;
    this.startedAt = now;
    return true;
  }

  /** The server refused the jump: land now rather than finish an arc nobody else sees. */
  cancel(): void {
    this.startedAt = 0;
  }

  offsetAt(now: number): number {
    return this.startedAt > 0 ? jumpOffsetY(now - this.startedAt) : 0;
  }

  airborneAt(now: number): boolean {
    return this.startedAt > 0 && isAirborne(now - this.startedAt);
  }
}
